import {
  ExpressCheckoutElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import React, { useState, useCallback, useMemo, useRef } from "react";
import { useRouter } from "next/navigation";
import type {
  StripeExpressCheckoutElementConfirmEvent,
  StripeExpressCheckoutElementOptions,
} from "@stripe/stripe-js";
import * as Sentry from "@sentry/nextjs";
import {
  STATSIG_EVENTS,
  useStatsigClient,
  type RegisteredStatsigEvent,
} from "@repo/statsig";
import { BRAND } from "@repo/tailwind-config/brand";
import { normalizeProductKey } from "../../lib/normalize-product";

const isOwlet = BRAND === "owlet";

interface PlanDetails {
  id: string;
  price: number;
  interval: "month" | "year";
  trialDays: number;
}

interface ExpressCheckoutButtonProps {
  successPath: string;
  errorPath: string;
  setIsProcessingPayment: (isProcessing: boolean) => void;
  planDetails: PlanDetails;
  productName: string;
  /** Identifies where the button is rendered (e.g. "paywall", "checkout") for analytics */
  source?: string;
  /** Called when Stripe reports whether Apple Pay / Google Pay is available */
  onAvailabilityChange?: (available: boolean) => void;
  userInfo: {
    email: string;
    name: string;
    metadata: Record<string, string>;
  };
  onCreateSetup: (userInfo: {
    email: string;
    name: string;
    metadata: Record<string, string>;
  }) => Promise<{ customerId: string; clientSecret: string }>;
}

/**
 * ExpressCheckoutButton - Implements Apple Pay and Google Pay with on-demand customer creation.
 *
 * Key features:
 * 1. Does not require pre-created customer or setup intent
 * 2. Creates customer and setup intent only when user clicks express button
 * 3. Better performance and no orphaned records
 * 4. Follows Stripe's recommended pattern
 */
const ExpressCheckoutButton: React.FC<ExpressCheckoutButtonProps> = ({
  successPath,
  errorPath,
  setIsProcessingPayment,
  planDetails,
  productName,
  source = "unknown",
  onAvailabilityChange,
  userInfo,
  onCreateSetup,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const router = useRouter();
  const [isInitializing, setIsInitializing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Drives the opacity crossfade: starts false on every mount (parents force a
  // remount via `key` when the price changes), flips true inside `handleReady`
  // once Stripe finishes mounting the iframe. Replaces the abrupt pop-in on top
  // of the skeleton underlay with a smooth fade (PE-1291).
  const [isReady, setIsReady] = useState(false);
  const { logEvent, client } = useStatsigClient();

  const handleExpressCheckout = useCallback(
    async (event: StripeExpressCheckoutElementConfirmEvent) => {
      if (!stripe || !elements) {
        console.error("Stripe not ready for express checkout");
        return;
      }

      // Prevent duplicate processing
      if (isInitializing) return;

      setIsProcessingPayment(true);
      setIsInitializing(true);
      setErrorMessage(null);

      try {
        Sentry.addBreadcrumb({
          message: "Express checkout initiated",
          category: "stripe",
          level: "info",
          data: {
            productName,
            plan: planDetails.interval,
            email: userInfo.email,
          },
        });

        // Merge wallet-provided billing details (from Apple Pay / Google Pay)
        // into userInfo so the Stripe customer gets a real name and email.
        const walletEmail = event.billingDetails?.email || userInfo.email;
        const walletName = event.billingDetails?.name || userInfo.name;
        const mergedUserInfo = {
          ...userInfo,
          email: walletEmail,
          name: walletName,
        };

        // Fire payment_submitted at click-intent — BEFORE onCreateSetup and
        // stripe.confirmSetup. The user has tapped the wallet confirm sheet
        // and committed; that's the funnel signal. Firing here means a
        // failure in onCreateSetup (e.g. Stripe API drift, customer-already-
        // subscribed, network) still leaves the front-half analytics intact.
        // Express checkout can be Apple Pay or Google Pay.
        const selectedPlan = userInfo.metadata?.selectedPlan || "";
        const taxonomyProductKey = normalizeProductKey(selectedPlan);
        const cadence = userInfo.metadata?.cadence || "monthly";
        const taxonomyBillingPeriod =
          cadence === "yearly" ? "annual" : "monthly";

        // Determine payment method from the event (Apple Pay vs Google Pay)
        const paymentMethod =
          event?.expressPaymentType === "google_pay"
            ? "google_pay"
            : event?.expressPaymentType === "apple_pay"
              ? "apple_pay"
              : "unknown";

        logEvent({
          eventName: STATSIG_EVENTS.PAYMENT_SUBMITTED,
          value: paymentMethod,
          metadata: {
            payment_method: paymentMethod,
            product_key: taxonomyProductKey,
            billing_period: taxonomyBillingPeriod,
            source,
            // Revenue metadata mirrors paywall cta_clicked + webhook
            // subscription_created so downstream Sum metrics work uniformly
            // across the funnel. planDetails.price is already in cents.
            price_cents: String(planDetails.price),
            implied_mrr_cents: String(
              planDetails.interval === "year"
                ? Math.round(planDetails.price / 12)
                : planDetails.price
            ),
            implied_immediate_revenue_cents: String(planDetails.price),
          },
        });
        // Signal the surrounding payment page so its unmount cleanup can
        // skip the checkout_abandoned event — the user did submit, even if
        // Stripe later rejects the card. Listener is in
        // `apps/platform/src/app/home/steps/payment/page.tsx`.
        window.dispatchEvent(new Event("statsig:payment_submitted"));

        // Create customer and setup intent on demand
        const result = await onCreateSetup(mergedUserInfo);
        const { customerId, clientSecret } = result;

        // If no clientSecret (e.g. customer already has an active subscription),
        // throw so the catch block handles cleanup and the payment sheet dismisses.
        if (!clientSecret) {
          throw new Error("You already have an active subscription.");
        }

        Sentry.addBreadcrumb({
          message: "Express checkout setup intent created",
          category: "stripe",
          level: "info",
          data: {
            customerId,
            hasClientSecret: !!clientSecret,
          },
        });

        // Set up return URL with customer ID
        const returnUrl = new URL(successPath, window.location.origin);
        returnUrl.searchParams.set("customer_id", customerId);
        returnUrl.searchParams.set("express", "true");

        // Flush queued Statsig events before Stripe redirects the page.
        // `stripe.confirmSetup({ redirect: "always" })` navigates the user
        // away as soon as Stripe accepts the wallet credentials. The
        // Statsig client batches events on a timer; without an explicit
        // flush, any events logged in this turn (payment_submitted from
        // handleExpressCheckout, express_checkout_confirmed from the
        // onConfirm sibling) can be discarded mid-navigation.
        //
        // Bounded by a 500ms race: the user has already approved the wallet
        // sheet, so analytics must never delay the redirect (Statsig's network
        // POST otherwise blocks up to its 10s timeout on a bad connection).
        // Anything not flushed in time falls back to the SDK's beacon-on-unload
        // and its persisted-logs retry on next session, so events aren't lost.
        await Promise.race([
          client.flush().catch((flushErr) => {
            Sentry.addBreadcrumb({
              message: "Statsig flush failed before Stripe redirect",
              category: "stripe",
              level: "warning",
              data: {
                error:
                  flushErr instanceof Error
                    ? flushErr.message
                    : String(flushErr),
              },
            });
          }),
          new Promise((resolve) => setTimeout(resolve, 500)),
        ]);

        // Use the setup intent client secret with confirmSetup
        // ExpressCheckoutElement works with setup intents for subscriptions
        const { error } = await stripe.confirmSetup({
          elements,
          clientSecret,
          confirmParams: {
            return_url: returnUrl.href,
            payment_method_data: {
              billing_details: {
                email: walletEmail,
                name: walletName,
                address: event.billingDetails?.address,
              },
            },
          },
          redirect: "always",
        });

        if (error) {
          console.error("Express checkout confirmation error:", error);
          setErrorMessage(error.message || "Failed to process express payment");

          Sentry.captureException(error, {
            tags: {
              component: "ExpressCheckoutButton",
              error_type: error.type,
              session_type: "setup_intent",
            },
            extra: {
              customerId,
              userEmail: userInfo.email,
            },
          });
        }
      } catch (error) {
        console.error("Express checkout error:", error);

        const message =
          error instanceof Error
            ? error.message
            : "Express checkout failed. Please try the regular payment form.";

        setErrorMessage(message);

        Sentry.captureException(error, {
          tags: {
            component: "ExpressCheckoutButton",
            error_type: "express_checkout_error",
          },
          extra: {
            userEmail: userInfo.email,
          },
        });

        // For critical errors, redirect to error page
        if (message.includes("network") || message.includes("timeout")) {
          const errorParams = new URLSearchParams({
            message: "Network error during express checkout",
            next: window.location.href,
          });
          router.push(`${errorPath}?${errorParams.toString()}`);
        }
      } finally {
        setIsProcessingPayment(false);
        setIsInitializing(false);
      }
    },
    [
      stripe,
      elements,
      isInitializing,
      userInfo,
      planDetails,
      productName,
      successPath,
      errorPath,
      setIsProcessingPayment,
      router,
      onCreateSetup,
      logEvent,
      client,
      source,
    ]
  );

  // Track loads for the v2 checkout_method_loaded event. The Express
  // Checkout element re-renders on cadence switch and Stripe re-fires
  // `onReady`; we count loads so dashboards can filter to first-load
  // (`load_count = "1"`) instead of needing fragile fire-once code.
  const expressLoadCountRef = useRef(0);

  // Handle element ready event — report availability and log for analytics
  const handleReady = useCallback(
    (event: any) => {
      const methods = event.availablePaymentMethods;
      const hasAny = !!(methods?.applePay || methods?.googlePay);
      onAvailabilityChange?.(hasAny);

      expressLoadCountRef.current += 1;
      logEvent({
        eventName: STATSIG_EVENTS.CHECKOUT_METHOD_LOADED,
        value: "express",
        metadata: {
          load_count: String(expressLoadCountRef.current),
          trigger:
            expressLoadCountRef.current === 1 ? "initial" : "cadence_switch",
          page_url:
            typeof window !== "undefined" ? window.location.href : undefined,
          source,
          // Normalize via normalizeProductKey so this metadata key matches
          // the Elements form's checkout_method_loaded shape (PE-1196 used
          // product_key snake_case canonical for Elements; Express was
          // missed at the time and shipped raw camelCase under `product`).
          product_key: normalizeProductKey(productName),
          billing_period:
            planDetails.interval === "year" ? "annual" : "monthly",
        },
      });

      Sentry.addBreadcrumb({
        message: "Express checkout element ready",
        category: "stripe",
        level: "info",
        data: {
          availablePaymentMethods: methods,
        },
      });

      setIsReady(true);
    },
    [onAvailabilityChange, logEvent, source, productName, planDetails.interval]
  );

  // ExpressCheckoutElement bakes `applePay` at mount (a create-only option).
  // react-stripe-js re-`update()`s whenever any options key deep-compares
  // unequal, and it compares Dates by reference — so a fresh `new Date()` each
  // render made `applePay` "change" every render, spamming
  // `expressCheckoutElement.update({applePay})`, which Stripe rejects as an
  // unrecognized update param (and wastes work). Memoize so the Date instances
  // (and the whole options object) stay stable; the element already remounts via
  // `key` on plan change (PaywallExpressCheckout), so `applePay` only needs to
  // exist at create.
  const expressCheckoutOptions = useMemo<StripeExpressCheckoutElementOptions>(() => {
    const displayName =
      productName === "pediatricSupport"
        ? "Owlet OnCall"
        : productName === "pediatricSupportSingle"
          ? "Owlet OnCall Single"
          : productName === "pediatricSupportFamily"
            ? "Owlet OnCall Family"
            : productName === "unlimitedRx"
              ? "Fast Prescriptions"
              : productName === "kinsaClinic"
                ? "Kinsa Clinic"
                : productName;
    const ONE_DAY_MS = 24 * 60 * 60 * 1000;
    const hasTrial = planDetails.trialDays > 0;
    const now = Date.now();
    // Apple rejects PKRecurringPaymentRequest when regularBilling start <= trial
    // start; buffer by 1 day when trialDays === 0 so the wallet button shows.
    const regularBillingStart = new Date(
      now + (hasTrial ? planDetails.trialDays : 1) * ONE_DAY_MS
    );
    const trialBillingStart = new Date(now);
    return {
      applePay: {
        recurringPaymentRequest: {
          managementURL: process.env.NEXT_PUBLIC_APP_URL
            ? `${process.env.NEXT_PUBLIC_APP_URL}/?felt-route=/home`
            : process.env.NEXT_PUBLIC_BRAND === "owlet"
              ? "https://owlet.felt.care/?felt-route=/home"
              : "https://polaris.kinsahealth.net/?felt-route=/home",
          paymentDescription: `${displayName} subscription`,
          regularBilling: {
            label: displayName,
            amount: planDetails.price,
            recurringPaymentStartDate: regularBillingStart,
            recurringPaymentIntervalUnit: planDetails.interval,
          },
          // Omit trialBilling when there's no trial — see the buffer comment.
          ...(hasTrial && {
            trialBilling: {
              label: `${displayName} Free Trial`,
              amount: 0,
              recurringPaymentStartDate: trialBillingStart,
              recurringPaymentIntervalUnit: planDetails.interval,
            },
          }),
        },
      },
      buttonHeight: 48,
      buttonTheme: {
        applePay: isOwlet ? "white" : "black",
        googlePay: isOwlet ? "white" : "black",
      },
      buttonType: {
        applePay: "subscribe",
        googlePay: "subscribe",
      },
      paymentMethods: {
        applePay: "auto",
        googlePay: "auto",
        link: "never",
        amazonPay: "never",
        paypal: "never",
      },
      layout: {
        maxColumns: 2,
        maxRows: 1,
      },
    };
  }, [
    planDetails.price,
    planDetails.interval,
    planDetails.trialDays,
    productName,
  ]);

  if (!stripe || !elements) {
    return null;
  }

  const sharedExpressMetadata = {
    description: "User has interacted with express checkout button",
    source,
    product: productName,
    billing_period: planDetails.interval === "year" ? "annual" : "monthly",
    // Revenue metadata mirrors paywall cta_clicked + webhook
    // subscription_created so funnel Sum metrics work uniformly.
    // planDetails.price is already in cents.
    price_cents: String(planDetails.price),
    implied_mrr_cents: String(
      planDetails.interval === "year"
        ? Math.round(planDetails.price / 12)
        : planDetails.price
    ),
    implied_immediate_revenue_cents: String(planDetails.price),
  };
  const expressCheckoutPressEvent: RegisteredStatsigEvent = {
    eventName: "express_checkout_pressed",
    metadata: {
      ...sharedExpressMetadata,
      description: "User has pressed express checkout button",
    },
  };
  const expressCheckoutConfirmEvent: RegisteredStatsigEvent = {
    eventName: "express_checkout_confirmed",
    metadata: {
      ...sharedExpressMetadata,
      description: "User has submitted express checkout payment",
    },
  };

  return (
    <div className="express-checkout-container">
      {errorMessage && (
        <div
          className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-4"
          role="alert"
          aria-live="polite"
        >
          <p className="text-sm">{errorMessage}</p>
        </div>
      )}

      <div
        className={`transition-opacity duration-300 ease-out ${
          isReady ? "opacity-100" : "opacity-0"
        }`}
      >
        <ExpressCheckoutElement
          onReady={handleReady}
          onCancel={() => {
            setIsProcessingPayment(false);
            setIsInitializing(false);

            Sentry.addBreadcrumb({
              message: "Express checkout cancelled",
              category: "stripe",
              level: "info",
            });
          }}
          onConfirm={(event) => {
            handleExpressCheckout(event);
            logEvent(expressCheckoutConfirmEvent);
          }}
          // Stripe's ExpressCheckoutElement exposes onClick for the wallet
          // button. Previously this listener was on onFocus, but mobile
          // Apple Pay / Google Pay flows do not reliably set focus before
          // opening the sheet (iOS Safari skips focus, WebKit's Apple Pay
          // handoff bypasses it), so onFocus produced zero events ever in
          // the Owlet Statsig project. onClick is the canonical press hook
          // and captures both desktop and mobile.
          onClick={({ resolve }) => {
            logEvent(expressCheckoutPressEvent);
            resolve();
          }}
          options={expressCheckoutOptions}
        />
      </div>
    </div>
  );
};

export default ExpressCheckoutButton;
